import React, { useEffect } from "react";
import { Link } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";
import { useTranslation } from "react-i18next";
import { Button } from "./ui/button";
import ExclusiveBadge from "./ExclusiveBadge";
import Header from "./Header";
import type { ErrorComponentProps } from "@tanstack/react-router";

interface GlobalErrorProps extends ErrorComponentProps {
  /**
   * Who rendered this screen, in the `feature` vocabulary the other capture
   * sites tag with. The router's `errorComponent` is only one caller: the home
   * and My List render it themselves when their query fails, and a hardcoded
   * tag claimed a router error for those too. Required, so the next inline
   * caller can't inherit the wrong answer by default.
   */
  feature: "router" | "home" | "my-list" | "actor";
}

const GlobalError: React.FC<GlobalErrorProps> = ({ error, feature }) => {
  const { t } = useTranslation();

  useEffect(() => {
    Sentry.captureException(error, {
      tags: {
        errorBoundary: "GlobalError",
        feature,
      },
      extra: {
        errorInfo: error instanceof Error ? error.stack : String(error),
      },
    });
  }, [error, feature]);

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-10 px-4 text-center">
      <Header className="fixed top-0 right-0 left-0 z-50" />
      <div className="flex max-w-sm flex-col items-center justify-center gap-2">
        <ExclusiveBadge className="mb-4 size-20 text-neutral-50" />
        <h1 className="text-2xl font-bold">{t("error_global_title")}</h1>
        <p className="text-muted-foreground text-md text-balance">
          {t("error_global_message")}
        </p>
      </div>
      <div className="flex w-full flex-col gap-2 md:flex-row md:justify-center">
        <Button className="w-full md:w-auto" asChild>
          <Link to="/">{t("error_global_home_button")}</Link>
        </Button>
        <Button
          variant="tertiary"
          className="w-full md:w-auto"
          onClick={() => window.location.reload()}
        >
          {t("error_global_retry_button")}
        </Button>
      </div>
    </div>
  );
};

export default GlobalError;
